/*
 Time tracking
*/
import React, { useEffect, useState } from 'react';
import { IPatient, GetFullName, getOnePatient } from '../../../libs/patient';
import { useAuth } from '../../../contexts/auth';
import { useMetaData } from '../../../contexts/metadata';
import Card, { CardBlock } from '../card';
import Dialog from '../dialog';
import Modal from '../modal';
import SelectPatientDrawer from '../patient/search';
import { Classes } from '@blueprintjs/core';
import { BaseResponse, PostJSON } from '../../../libs/request';
import {
  faPause,
  faPlay,
  faStopwatch,
  faWindowClose
} from '@fortawesome/pro-solid-svg-icons';
import Icon from '../../atoms/icon';
import Tag from '../../atoms/tag';
import Button from '../../molecules/button';
import { Form, FormInstance } from '@lightbeamhealth/blueprint-form';
import { Col, Container, Row } from '../../../grid';
import { Link, useLocation } from 'react-router-dom';
import { FormatDOB, FormatPhone } from '../../../libs/format';
import { TalkSession, SessionManager } from '../../../libs/track';
import './talk.scss';

enum TrackTimeButtonState {
  hidden,
  timer,
  default,
  default_hover
}

const getPatientIdFromURL = (query: URLSearchParams) => {
  if (
    location.pathname.split('/')[1] === 'patient' &&
    !isNaN(Number(location.pathname.split('/')[2]))
  ) {
    return location.pathname.split('/')[2];
  } else if (query.get('patientId')) {
    return query.get('patientId');
  } else {
    return null;
  }
};

const getTags = (patient: IPatient): React.ReactNodeArray => {
  const tags: React.ReactNodeArray = [];
  const parts = patient.tags ? patient.tags.split(',') : [];
  for (const [i, part] of parts.entries()) {
    tags.push(<Tag key={i} text={part} />);
  }
  return tags;
};

export const TrackTimePatientPlaceholderCard = (): JSX.Element => {
  return (
    <span
      style={{
        display: 'inline-block',
        border: '1px solid #CCCCCC',
        height: '83px',
        width: '600px',
        borderRadius: '5px'
      }}
    ></span>
  );
};

interface PatientCardProps {
  patient: IPatient;
  close: () => void;
}

export const TrackTimePatientCard = ({
  patient,
  close
}: PatientCardProps): JSX.Element => {
  const { terms } = useMetaData();
  return (
    <Card className="py-4 px-0" style={{ minWidth: '600px' }}>
      <div>
        <Container fluid>
          <Row>
            <Col sm={16} md={18}>
              <div className="inline-block mr-2"></div>
              <Link
                to={`/patient/${patient.id}`}
                className="font-bold pr-3"
                onClick={close}
              >
                {GetFullName(patient)}
              </Link>
              <span className="font-bold pr-3">
                {FormatPhone(patient.phone)}
              </span>
              {patient.dob && <span>DOB: {FormatDOB(patient.dob)}</span>}
              <br />
              <Tag
                text={`CSID: ${patient.id}`}
                style={{
                  marginLeft: 0,
                  marginRight: '10px'
                }}
              />
              <Tag
                text={`${terms.mrn.capitalized}: ${patient.mrn}`}
                style={{
                  marginLeft: 0,
                  marginRight: '10px'
                }}
              />
              {patient.tags ? (
                <>
                  <span className="ml-4">Tags:</span> {getTags(patient)}
                </>
              ) : null}
              <br />
            </Col>
          </Row>
        </Container>
      </div>
    </Card>
  );
};

const TrackTime = (): JSX.Element => {
  const { user } = useAuth();
  const { account } = useMetaData();
  const [session, setSession] = useState(new TalkSession({ userId: user.id }));
  const [isShowingDetails, setIsShowingDetails] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [poll, setPoll] = useState(0 as number);
  const [patient, setPatient] = useState(null as IPatient);
  const [note, setNote] = useState('');
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [isStopped, setIsStopped] = useState(true);
  const [lastPolledAt, setLastPolledAt] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPatientDrawerOpen, setIsPatientDrawerOpen] = useState(false);
  const { terms } = useMetaData();
  const form = new FormInstance({ note: '' });
  const { search, pathname } = useLocation();
  // TODO: Replace with React Router's useSearchParams once we upgrade to react-router-dom v6
  function useSearchParams() {
    return React.useMemo(() => new URLSearchParams(search), [search]);
  }
  const query = useSearchParams();

  const autoloadPatientFromURL = async (session: TalkSession) => {
    const patientIdFromURL = getPatientIdFromURL(query);
    if (patientIdFromURL) {
      const { patient: patientFromURL } = await getOnePatient(
        parseInt(patientIdFromURL, 10) || null,
        true
      );
      setPatient((prevPatient) => {
        if (patientFromURL?.id !== prevPatient?.id) {
          session.patient = patientFromURL;
          return patientFromURL;
        } else {
          session.patient = prevPatient;
          return prevPatient;
        }
      });
    }
  };

  useEffect(() => {
    (async () => {
      const _session =
        (await restartPreviousSession()) ||
        new TalkSession({ userId: user.id });
      setSession(_session);
      if (!_session.duration) {
        autoloadPatientFromURL(_session);
      }
    })();
  }, []);

  // For loading patient from /?patientId=ID page
  useEffect(() => {
    setSession((latestSession) => {
      if (latestSession && !latestSession?.duration) {
        autoloadPatientFromURL(latestSession);
      }
      return latestSession;
    });
  }, [query]);

  // For loading patient from /patient/ID page
  useEffect(() => {
    // React Router v6 doesn't have history.listen, so we handle this via pathname changes
    if (!query.get('patientId')) {
      setSession((latestSession) => {
        if (latestSession && !latestSession?.duration) {
          autoloadPatientFromURL(latestSession);
        }
        return latestSession;
      });
    }
  }, [pathname]);

  // When the timer is running (when isStopped is false), send its value to the controls' state
  useEffect(() => {
    setMinutes((prevMinutes) => {
      if (
        session &&
        !session?.isStopped &&
        session?.splitDuration?.minutes !== prevMinutes
      ) {
        return session.splitDuration.minutes;
      } else {
        return prevMinutes;
      }
    });

    setSeconds((prevSeconds) => {
      if (
        session &&
        !session?.isStopped &&
        session?.splitDuration?.seconds !== prevSeconds
      ) {
        return session.splitDuration.seconds;
      } else {
        return prevSeconds;
      }
    });
  }, [lastPolledAt]);

  const restartPreviousSession = async () => {
    const _session = await SessionManager.loadSession(user.id);
    if (_session) {
      setPatient(_session.patient);
      setNote(_session.note);
      setMinutes(_session.splitDuration.minutes);
      setSeconds(_session.splitDuration.seconds);
      setIsStopped(_session.isStopped);
      const _poll = SessionManager.startPoll(_session, setLastPolledAt);
      setPoll(_poll);
      return _session;
    } else {
      return null;
    }
  };

  const close = () => {
    setIsShowingDetails(false);
  };

  const open = () => {
    setIsShowingDetails(true);
  };

  const resetSession = async () => {
    await SessionManager.clearSession(poll);
    session.reset(); // Just in case
    setPatient(null as IPatient);
    setNote('');
    setMinutes(0);
    setSeconds(0);
    setIsStopped(true);
    setPoll(0);
    setSession(() => {
      const newSession = new TalkSession({ userId: user.id });
      autoloadPatientFromURL(newSession);
      return newSession;
    });
  };

  const updatePatient = (patient: IPatient) => {
    setPatient(patient);
    session.patient = patient;
  };

  const addEntry = async () => {
    if (!session?.isStopped) {
      setIsSubmitting(false);
      throw new Error('Cannot add time entry while session is running!');
    }
    setIsSubmitting(true);
    const { duration, patient, note } = session;
    const response = await PostJSON<BaseResponse>({
      url: '/talk/create/',
      params: {
        duration,
        initiatedByProvider: true,
        patient: patient.id,
        note
      }
    });
    if (response.status === 'OK') {
      close();
      await resetSession();
      Modal.success({
        title: 'Entry Added',
        content: 'The entry has been successfully created!'
      });
    } else {
      Modal.error({
        title: 'Error',
        content:
          response.message ||
          'We were unable to add your time entry at this time.'
      });
    }
    setIsSubmitting(false);
  };

  const openPatientDrawer = () => {
    setIsPatientDrawerOpen(true);
  };

  const startPoll = (prevPoll: number) => {
    if (!prevPoll) {
      const _poll = SessionManager.startPoll(session, setLastPolledAt);
      session.start();
      return _poll;
    } else {
      session.start();
      return 0;
    }
  };

  const togglePlayPause = () => {
    setIsStopped((prevIsStopped) => {
      if (prevIsStopped) {
        setPoll(startPoll);
        return false;
      } else {
        session.stop();
        return true;
      }
    });
  };

  const updateMinutes = ({ target: { value } }) => {
    setMinutes(parseInt(value.replaceAll('.', '')) || 0);
  };

  const updateSeconds = ({ target: { value } }) => {
    setSeconds(parseInt(value.replaceAll('.', '')) || 0);
  };

  const updateSessionMinutes = ({ target: { value } }) => {
    setSession((prevSession) => {
      prevSession.accumulatedDuration =
        prevSession.splitDuration.seconds +
        Math.floor(parseInt(value, 10) * 60);
      return prevSession;
    });
  };

  const updateSessionSeconds = ({ target: { value } }) => {
    setSession((prevSession) => {
      prevSession.accumulatedDuration =
        prevSession.splitDuration.minutes * 60 + parseInt(value, 10);
      return prevSession;
    });
  };

  const updateNote = ({ target: { value } }) => {
    setNote(value);
    setSession((prevSession) => {
      prevSession.note = value;
      return prevSession;
    });
  };

  const getButtonState = (): TrackTimeButtonState => {
    if (isShowingDetails) {
      return TrackTimeButtonState.hidden;
    }
    if (lastPolledAt && session?.duration) {
      return TrackTimeButtonState.timer;
    }
    if (isHovered) {
      return TrackTimeButtonState.default_hover;
    }
    return TrackTimeButtonState.default;
  };

  const isIconMarginRequired = [
    TrackTimeButtonState.timer,
    TrackTimeButtonState.default_hover
  ].includes(getButtonState());

  const TrackTimeButtonText = () => {
    if (getButtonState() === TrackTimeButtonState.timer) {
      return (
        <span className="timer">
          <span>{minutes}</span>
          <span className="colon">:</span>
          <span>{seconds < 10 ? `0${seconds}` : seconds}</span>
          <span> with </span>
          <span>
            {patient.firstName} {patient.lastName[0]}.
          </span>
        </span>
      );
    } else if (getButtonState() === TrackTimeButtonState.default_hover) {
      return <span>Track Time</span>;
    } else {
      return null;
    }
  };
  const TrackTimeButton = () => {
    return (
      <Button
        intent="primary"
        onClick={() => {
          setIsHovered(false);
          open();
        }}
        className="text-center"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        title="Track Time"
      >
        <Icon
          icon={faStopwatch}
          className={`text-white ${isIconMarginRequired ? 'mr-2' : ''}`}
        />
        <TrackTimeButtonText />
      </Button>
    );
  };

  // For now, hide this component on the chat center page
  const disablePath = '/chat/';
  if (pathname.substring(0, disablePath.length) === disablePath) {
    return null;
  }
  if (!account?.isTimeTrackedEnabled) return null;
  return (
    <div className="fixed z-30" style={{ bottom: '90px', right: '26px' }}>
      {!isShowingDetails ? <TrackTimeButton /> : null}
      <Dialog
        portalClassName="z-30"
        isOpen={isShowingDetails}
        onClose={close}
        forceScrollY
      >
        <div
          className={Classes.DIALOG_HEADER}
          style={{ justifyContent: 'space-between' }}
        >
          <h3>
            <Icon icon={faStopwatch} className="text-black text-sans-lg mr-2" />
            Track Time Spent on {terms.patient.capitalized}
          </h3>
          <Button
            style={{ border: '0', boxShadow: 'none', background: 'none' }}
            onClick={close}
          >
            <Icon icon={faWindowClose} className="text-black" />
          </Button>
        </div>
        <div className={Classes.DIALOG_BODY}>
          <Form form={form} onSubmit={addEntry}>
            <CardBlock>
              <Row>
                <Col sm={6}>
                  <span
                    style={{
                      fontWeight: 600,
                      display: 'inline-block'
                    }}
                    className="bp5-label mb-2"
                  >
                    {terms.patient.capitalized}
                  </span>
                </Col>
              </Row>
              <Row
                style={{
                  alignItems: 'center',
                  marginBottom: '30px',
                  justifyContent: 'space-between'
                }}
              >
                <Col>
                  {patient ? (
                    <TrackTimePatientCard patient={patient} close={close} />
                  ) : (
                    <TrackTimePatientPlaceholderCard />
                  )}
                </Col>
                <Col style={{ textAlign: 'right', padding: '0px' }}>
                  <Button id="timeTrackPatient" onClick={openPatientDrawer}>
                    {patient ? 'Change' : 'Choose'} {terms.patient.capitalized}
                  </Button>
                </Col>
              </Row>
              <Row>
                <Col sm={6}>
                  <span
                    style={{
                      fontWeight: 600,
                      display: 'inline-block'
                    }}
                    className="bp5-label mb-2"
                  >
                    Time Elapsed
                  </span>
                </Col>
              </Row>
              <Row style={{ marginBottom: '30px' }}>
                <Col>
                  {isStopped ? (
                    // Updateable by user
                    <input
                      style={{ width: '50px' }}
                      className="bp5-input text-right"
                      name="timeElapsedMin"
                      placeholder={`${minutes}`}
                      value={minutes}
                      min="0"
                      onChange={updateMinutes}
                      onBlur={updateSessionMinutes}
                      type="number"
                    />
                  ) : (
                    // Just showing timer's value
                    <input
                      style={{ width: '50px' }}
                      className="bp5-input text-right"
                      name="timeElapsedMin"
                      placeholder="0"
                      value={minutes}
                      readOnly
                    />
                  )}{' '}
                  min :
                  {session?.isStopped ? (
                    // Updateable by user
                    <input
                      style={{ width: '50px' }}
                      className="bp5-input text-right"
                      name="timeElapsedSec"
                      placeholder={`${seconds}`}
                      value={seconds}
                      min="0"
                      max="59"
                      onChange={updateSeconds}
                      onBlur={updateSessionSeconds}
                      type="number"
                    />
                  ) : (
                    // Just showing timer's value
                    <input
                      style={{ width: '50px' }}
                      className="bp5-input text-right"
                      name="timeElapsedSec"
                      placeholder="00"
                      value={seconds}
                      readOnly
                    />
                  )}{' '}
                  sec
                  {patient ? (
                    <Button className="ml-4" onClick={togglePlayPause}>
                      {isStopped ? 'Start' : 'Pause'} Timer
                      <Icon
                        className="ml-3"
                        icon={isStopped ? faPlay : faPause}
                      />
                    </Button>
                  ) : null}
                </Col>
              </Row>
              <Row>
                <Col sm={6}>
                  <span
                    style={{
                      fontWeight: 600,
                      display: 'inline-block'
                    }}
                    className="bp5-label mb-2"
                  >
                    Note (optional)
                  </span>
                </Col>
              </Row>
              <Row>
                <Col>
                  <textarea
                    name="note"
                    className="bp5-input w-full"
                    style={{ height: '100px' }}
                    onChange={updateNote}
                    onBlur={updateNote}
                    value={note}
                  />
                </Col>
              </Row>
            </CardBlock>

            <div
              className={Classes.DIALOG_FOOTER}
              style={{ textAlign: 'right' }}
            >
              <Button
                style={{ float: 'left' }}
                type="button"
                onClick={resetSession}
              >
                Clear Tracker
              </Button>

              <Button
                intent="success"
                type="submit"
                disabled={
                  (minutes === 0 && seconds === 0) ||
                  !session?.isStopped ||
                  isSubmitting
                }
              >
                Add Entry
              </Button>
            </div>
          </Form>
        </div>
        {isPatientDrawerOpen ? (
          <SelectPatientDrawer
            isOpen={isPatientDrawerOpen}
            setIsOpen={(isOpen) => setIsPatientDrawerOpen(isOpen)}
            setPatient={updatePatient}
          />
        ) : null}
      </Dialog>
    </div>
  );
};

export default TrackTime;
